import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/hooks/useStore";
import { useProducts } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductCard } from "@/components/product/ProductCard";
import { Loader2, Grid, List, PlusCircle } from "lucide-react";
import type { Product, ProductFilters, ProductSortField } from "@/types/product";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from '@/components/florist-dashboard/DashboardLayout';

interface SortOption {
  label: string;
  value: string;
  field: ProductSortField;
  direction: "asc" | "desc";
}

const sortOptions: SortOption[] = [
  { label: "Newest", value: "newest", field: "created_at", direction: "desc" },
  { label: "Oldest", value: "oldest", field: "created_at", direction: "asc" },
  { label: "Name: A to Z", value: "name_asc", field: "title", direction: "asc" },
  { label: "Name: Z to A", value: "name_desc", field: "title", direction: "desc" },
];

export default function Products() {
  const navigate = useNavigate();
  const { user, isFlorist, loading: authLoading } = useAuth();
  const { data: store, isLoading: storeLoading } = useStore();
  const [view, setView] = useState<"grid" | "list">("grid");
  const [filters, setFilters] = useState<ProductFilters>({});
  const [sort, setSort] = useState<SortOption>(sortOptions[0]);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    products,
    isLoading: productsLoading,
    error,
  } = useProducts({
    storeId: store?.id || "",
    filters: {
      ...filters,
      search: searchQuery,
    },
    sort: {
      field: sort.field,
      direction: sort.direction,
    },
    enabled: !authLoading && !!user && !!isFlorist && !!store?.id
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isFlorist) {
      navigate("/");
      return;
    }
  }, [user, isFlorist, authLoading, navigate]);

  const handleDeleteProduct = async (productId: string) => {
    try {
      // Delete the product from Supabase
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);

      if (error) {
        throw error;
      }

      // Show success message
      toast.success("Product deleted successfully");

      // Refresh the products list
      if (products) {
        // Force a re-fetch of the products
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product. Please try again.");
    }
  };

  if (authLoading || storeLoading || (productsLoading && !products)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Products</h1>
          <p className="text-brand-primary/70 mt-1">
            Manage your store's product catalog
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-brand-primary/10 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-brand-primary/10 focus-visible:ring-[brand-primary]/20 focus-visible:border-brand-primary/30"
              />
            </div>
            <div className="flex gap-4">
              <Select
                value={sort.value}
                onValueChange={(value) => {
                  const newSort = sortOptions.find((option) => option.value === value);
                  if (newSort) setSort(newSort);
                }}
              >
                <SelectTrigger className="w-[180px] border-brand-primary/10">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant={view === "grid" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setView("grid")}
                  className={view === "grid" ? "bg-[#967F81] hover:bg-[#843329]" : "border-brand-primary/10 text-brand-primary/70 hover:text-brand-primary hover:bg-[#967F81]/5"}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={view === "list" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setView("list")}
                  className={view === "list" ? "bg-[#967F81] hover:bg-[#843329]" : "border-brand-primary/10 text-brand-primary/70 hover:text-brand-primary hover:bg-[#967F81]/5"}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end mb-6">
            <Button 
              onClick={() => navigate("/products/new")}
              className="bg-[#967F81] hover:bg-[#843329]"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add New Product
            </Button>
          </div>

          {error ? (
            <div className="bg-red-50 p-6 rounded-lg border border-red-100 text-center text-red-500">
              Failed to load products. Please try again later.
            </div>
          ) : products?.length === 0 ? (
            <div className="py-12 bg-[#967F81]/5 rounded-lg border border-brand-primary/10 text-center">
              <p className="text-brand-primary/70 font-medium mb-4">No products found</p>
              <Button
                onClick={() => navigate("/products/new")}
                className="bg-[#967F81] hover:bg-[#843329]"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Your First Product
              </Button>
            </div>
          ) : (
            <div
              className={
                view === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  : "space-y-4"
              }
            >
              {products?.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  view={view}
                  onDelete={handleDeleteProduct}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
} 